/**
 * LabelLayoutEditor
 *
 * Main editor component that composes all sub-components:
 * - LabelCanvas: Visual canvas with grid
 * - DraggableComponent: Draggable/resizable component wrapper
 * - ComponentRenderer: Renders component content
 * - ComponentPanel: Visibility/lock toggles
 * - TemplateSelector: Template management
 * - EditorToolbar: Zoom, grid, undo/redo controls
 *
 * Features:
 * - beforeunload protection for unsaved changes
 * - Keyboard shortcuts (Ctrl+Z/Shift+Z for undo/redo)
 * - Print preview dialog at 1:1 scale
 * - Seeds built-in templates on first org access
 * - Loading and error states
 * - Click outside to deselect
 */

import { useEffect, useCallback, useState, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { trpc } from '@/lib/trpc';
import { useLabelLayoutStore } from './label-layout-store';
import { LabelCanvas } from './LabelCanvas';
import { DraggableComponent } from './DraggableComponent';
import { ComponentRenderer } from './ComponentRenderer';
import { HiddenComponentGhost } from './HiddenComponentGhost';
import { ComponentPanel } from './ComponentPanel';
import { TemplateSelector } from './TemplateSelector';
import { EditorToolbar } from './EditorToolbar';
import { DEFAULT_SAMPLE_DATA, PX_PER_MM, COMPONENT_LABEL_KEYS, validateScannability } from './label-layout-types';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, Eye, Info, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

/**
 * Main label layout editor component.
 * Combines canvas, component panel, template selector, and toolbar.
 * Includes beforeunload protection and print preview.
 */
export function LabelLayoutEditor() {
  const { t } = useTranslation('settings');
  const [previewOpen, setPreviewOpen] = useState(false);
  const [initError, setInitError] = useState<string | null>(null);

  const components = useLabelLayoutStore((s) => s.components);
  const canvasSize = useLabelLayoutStore((s) => s.canvasSize);
  const labelType = useLabelLayoutStore((s) => s.labelType);
  const scale = useLabelLayoutStore((s) => s.editor.scale);
  const isDirty = useLabelLayoutStore((s) => s.isDirty);
  const setTemplate = useLabelLayoutStore((s) => s.setTemplate);
  const setSelectedComponent = useLabelLayoutStore((s) => s.setSelectedComponent);

  // Seed built-in templates on first load if none exist
  const seedMutation = trpc.labelTemplates.seedDefaults.useMutation();
  const {
    data: templates,
    isLoading,
    error: fetchError,
    refetch,
  } = trpc.labelTemplates.list.useQuery();

  // Track initial canvas size and label type for template matching on first load
  // Using refs prevents stale closure issues and avoids re-running the effect
  const initialCanvasSizeRef = useRef(canvasSize);
  const initialLabelTypeRef = useRef(labelType);
  const hasInitializedRef = useRef(false);

  // Get templateId from store to detect when store has been reset
  const templateId = useLabelLayoutStore((s) => s.templateId);

  // Load default template on mount (runs once when templates load)
  useEffect(() => {
    // Reset initialization flag if store was reset (templateId became null)
    // This allows re-initialization if user navigates away and back
    if (templateId === null) {
      hasInitializedRef.current = false;
    }

    if (!isLoading && templates && !hasInitializedRef.current) {
      hasInitializedRef.current = true;

      if (templates.length === 0) {
        // Seed built-in templates for this organization
        seedMutation.mutate(undefined, {
          onSuccess: (seeded) => {
            const defaultTemplate = seeded.find((t) => t.isDefault) ?? seeded[0];
            if (defaultTemplate) {
              setTemplate({
                id: defaultTemplate.id,
                name: defaultTemplate.name,
                canvasSize: defaultTemplate.canvasSize,
                components: defaultTemplate.components,
                labelType: defaultTemplate.labelType,
                isDefault: defaultTemplate.isDefault,
                isBuiltIn: defaultTemplate.isBuiltIn,
              });
            }
            refetch();
          },
          onError: (err) => {
            setInitError(`Failed to initialize templates: ${err.message}`);
          },
        });
      } else {
        // Load default template for initial canvas size and label type
        const initialCanvas = initialCanvasSizeRef.current;
        const initialLabel = initialLabelTypeRef.current;
        const defaultForSize =
          templates.find(
            (t) =>
              !t.isBuiltIn &&
              t.isDefault &&
              t.canvasSize.width === initialCanvas.width &&
              t.canvasSize.height === initialCanvas.height &&
              (t.labelType === initialLabel || t.labelType === 'BOTH')
          ) ||
          templates.find(
            (t) =>
              t.isDefault &&
              t.canvasSize.width === initialCanvas.width &&
              t.canvasSize.height === initialCanvas.height &&
              (t.labelType === initialLabel || t.labelType === 'BOTH')
          );
        const template = defaultForSize || templates[0];
        if (template) {
          setTemplate({
            id: template.id,
            name: template.name,
            canvasSize: template.canvasSize,
            components: template.components,
            labelType: template.labelType,
            isDefault: template.isDefault,
            isBuiltIn: template.isBuiltIn,
          });
        }
      }
    }
  }, [isLoading, templates, templateId, seedMutation, setTemplate, refetch]);

  // Reset initialization ref on unmount to handle component remounting
  useEffect(() => {
    return () => {
      hasInitializedRef.current = false;
    };
  }, []);

  // Note: Keyboard shortcuts (Ctrl+Z, arrow keys, etc.) are handled by EditorToolbar
  // to avoid duplicate event listeners

  // beforeunload protection for unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty) {
        e.preventDefault();
        // Modern browsers ignore custom messages, but we still need returnValue
        e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
        return e.returnValue;
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  // Click outside to deselect
  const handleCanvasClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) {
        setSelectedComponent(null);
      }
    },
    [setSelectedComponent]
  );

  // Memoize filtered component arrays to prevent new array references on every render
  // This helps child components' React.memo work correctly
  const visibleComponents = useMemo(
    () => components.filter((c) => c.visible),
    [components]
  );
  const hiddenComponents = useMemo(
    () => components.filter((c) => !c.visible),
    [components]
  );

  // Get validation errors for top-level banner
  const validationResult = useMemo(() => {
    return validateScannability(components, canvasSize, labelType);
  }, [components, canvasSize, labelType]);

  // Loading state
  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
        <div className="flex items-center justify-center py-8">
          <div className="flex flex-col items-center gap-3 text-muted-foreground">
            <Loader2 className="h-8 w-8 animate-spin" />
            <p className="text-sm font-medium">{t('production.labelLayoutEditor.loadingTemplates')}</p>
          </div>
        </div>
        <div className="flex gap-6">
          <Skeleton className="flex-1 h-[400px]" />
          <Skeleton className="w-64 h-[400px]" />
        </div>
      </div>
    );
  }

  // Error state
  if (fetchError || initError) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          {fetchError?.message || initError}
          <Button
            variant="link"
            className="ml-2 p-0 h-auto"
            onClick={() => refetch()}
          >
            {t('production.labelLayoutEditor.tryAgain')}
          </Button>
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-4">
      {/* Top-level validation error banner */}
      {validationResult.structuredErrors.length > 0 && (
        <Alert variant="destructive" className="border-destructive/50 bg-destructive/5">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            <div className="font-medium mb-1">{t('production.labelLayoutEditor.validationErrors')}</div>
            <ul className="list-disc list-inside space-y-0.5 text-sm">
              {validationResult.structuredErrors.map((error, i) => {
                const translationKey = `production.validation.codes.${error.code}`;
                const componentName = error.componentId
                  ? t(COMPONENT_LABEL_KEYS[components.find(c => c.id === error.componentId)?.type ?? 'qr'])
                  : undefined;
                const translated = t(translationKey, {
                  componentName,
                  labelType,
                  defaultValue: error.message,
                });
                return <li key={i}>{translated}</li>;
              })}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      {/* Header with template selector */}
      <TemplateSelector />

      {/* Toolbar */}
      <div className="flex items-center gap-2">
        <div className="flex-1">
          <EditorToolbar />
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setPreviewOpen(true)}
          title={t('production.labelLayoutEditor.printPreview')}
        >
          <Eye className="h-4 w-4 mr-1" />
          {t('production.labelLayoutEditor.preview')}
        </Button>
      </div>

      {/* Main editor area */}
      <div className="flex gap-6">
        {/* Canvas wrapped in Card for visual hierarchy */}
        <Card className="flex-1 overflow-hidden">
          <CardContent className="p-0" onClick={handleCanvasClick}>
            <LabelCanvas>
              {/* Visible components */}
              {visibleComponents.map((comp) => (
                <DraggableComponent key={comp.id} config={comp}>
                  <ComponentRenderer
                    type={comp.type}
                    data={DEFAULT_SAMPLE_DATA}
                    width={comp.size.width * PX_PER_MM * scale}
                    height={comp.size.height * PX_PER_MM * scale}
                  />
                </DraggableComponent>
              ))}

              {/* Ghost placeholders for hidden components */}
              {hiddenComponents.map((comp) => (
                <HiddenComponentGhost key={comp.id} config={comp} />
              ))}
            </LabelCanvas>
          </CardContent>
        </Card>

        {/* Side panel */}
        <div className="w-64 shrink-0">
          <ComponentPanel />
        </div>
      </div>

      {/* Print Preview Dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        {/* dialog-overflow-ok: content-sized dialog (1:1 label preview) keeps an auto track so it sizes to the preview */}
        <DialogContent className="max-w-fit grid-cols-[auto]">
          <DialogHeader>
            <DialogTitle>{t('production.labelLayoutEditor.printPreview')}</DialogTitle>
          </DialogHeader>
          {/* Explanation text */}
          <div className="flex items-start gap-2 p-3 bg-muted/50 rounded-lg text-sm text-muted-foreground">
            <Info className="h-4 w-4 mt-0.5 shrink-0" />
            <p>
              {t('production.labelLayoutEditor.printPreviewDesc', { width: canvasSize.width, height: canvasSize.height })}
            </p>
          </div>
          {/* Preview container with subtle background for contrast */}
          <div className="p-6 bg-muted/30 rounded-lg flex items-center justify-center">
            {/* Label card with shadow for visual depth */}
            <div className="p-4 bg-white rounded-md shadow-lg border-2 border-border">
              {/* Render at 1:1 scale (no editor scaling) */}
              <div
                style={{
                  width: canvasSize.width * PX_PER_MM,
                  height: canvasSize.height * PX_PER_MM,
                  position: 'relative',
                  backgroundColor: 'white',
                }}
              >
                {visibleComponents
                  .sort((a, b) => (a.zIndex ?? 1) - (b.zIndex ?? 1))
                  .map((comp) => (
                    <div
                      key={comp.id}
                      style={{
                        position: 'absolute',
                        left: comp.position.x * PX_PER_MM,
                        top: comp.position.y * PX_PER_MM,
                        width: comp.size.width * PX_PER_MM,
                        height: comp.size.height * PX_PER_MM,
                      }}
                    >
                      <ComponentRenderer
                        type={comp.type}
                        data={DEFAULT_SAMPLE_DATA}
                        width={comp.size.width * PX_PER_MM}
                        height={comp.size.height * PX_PER_MM}
                      />
                    </div>
                  ))}
              </div>
            </div>
          </div>
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>{t('production.labelLayoutEditor.scalePreview')}</span>
            <span className="font-medium">
              {canvasSize.width}mm × {canvasSize.height}mm
            </span>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
